<script setup lang="ts">
import { useLayout } from '@/layout/composables/layout'
import AppConfigurator from './AppConfigurator.vue'
import { usePrimeVue } from 'primevue'
import { useAuthStore } from '@/stores/authStore'
import { useConfirm } from 'primevue/useconfirm'
import router from '@/router'
import { onBeforeMount, ref, watch } from 'vue'
import {
  isUserLoggedSuperAdmin,
  isUserLoggedSuperAdminOrAdmin,
  logOnDev
} from '@/assets/js/main.ts'
import type { Device } from '@/models/Device.ts'
import MyAutocompletePaginated from '@/components/MyAutocompletePaginated.vue'
import { useDeviceStore } from '@/stores/deviceStore.ts'
import { PER_PAGE_NOT_PAGINATED } from '@/constants/commons.ts'
import { useTopbarStore } from '@/stores/topbarStore.ts'
import type { User } from '@sentry/vue'

const { toggleMenu, toggleDarkMode, isDarkTheme } = useLayout()

const currentUserUsername = ref({})
const currentUserRole = ref({})
const confirm = useConfirm()
const primeVue = usePrimeVue()

const topbarStore = useTopbarStore()
const localCustomerSelected = ref<User | null>(null)
const localDeviceSelected = ref<Device | null>(null)
const devicesParams = ref(new Map())
const allUserDevices = ref<Device[]>([])
const userDevices = ref<Device[]>([])

onBeforeMount(async () => {
  currentUserUsername.value = useAuthStore().userUsername()
  currentUserRole.value = useAuthStore().userRole()
})

watch(localCustomerSelected, async () => {
  if (localCustomerSelected.value?.id) {
    // manage it
    logOnDev(`APP TOP BAR => USER SELECTED: ${localCustomerSelected.value.id}`)
    // UPDATE STORE VARIABLE ONLY HERE, NOT BEFORE
    topbarStore.customerSelected = localCustomerSelected.value
    // set id
    devicesParams.value.set('userId', localCustomerSelected.value.id)
    // reset array
    userDevices.value = []
    // get devices
    let res = await useDeviceStore().getItems(
      0,
      PER_PAGE_NOT_PAGINATED,
      null,
      null,
      devicesParams.value,
      null
    )
    allUserDevices.value = res.data
    userDevices.value = res.data
  } else {
    topbarStore.customerSelected = null
  }
})

watch(localDeviceSelected, () => {
  if (localDeviceSelected.value?.id) {
    // manage it
    logOnDev(`APP TOP BAR => DEVICE SELECTED: ${localDeviceSelected.value.id}`)
    // UPDATE STORE VARIABLE ONLY HERE, NOT BEFORE
    topbarStore.deviceSelected = localDeviceSelected.value
  } else {
    topbarStore.deviceSelected = null
  }
})

const onReset = () => {
  localCustomerSelected.value = null
  localDeviceSelected.value = null
  userDevices.value = []
  allUserDevices.value = []
  topbarStore.reset()
}

const filterDevices = (event: { query: string }) => {
  const q = event.query.toLowerCase()
  userDevices.value = q
    ? allUserDevices.value.filter(
        (d) =>
          d.serialNumber?.toLowerCase().includes(q) ||
          d.vehicleId?.toLowerCase().includes(q)
      )
    : [...allUserDevices.value]
}

const onLogout = (event: any) => {
  confirm.require({
    target: event.currentTarget,
    message: primeVue.config.locale?.logout_dialog.message,
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: primeVue.config.locale?.commons.yes.toUpperCase(),
    rejectLabel: primeVue.config.locale?.commons.no.toUpperCase(),
    accept: async () => {
      await useAuthStore().logout()
      await router.push('/login')
    },
    reject: () => {}
  })
}

const onProfile = async () => {
  await router.push({
    name: 'profile'
  })
}
</script>

<template>
  <ConfirmPopup />
  <div class="layout-topbar">
    <!-- BURGER + NAME -->
    <div class="layout-topbar-logo-container">
      <button
        class="layout-menu-button layout-topbar-action"
        @click="toggleMenu"
      >
        <i class="pi pi-bars"></i>
      </button>
      <router-link to="/" class="layout-topbar-logo">
        <span> Portale Accise </span>
      </router-link>
    </div>

    <!-- USER LOGGED INFO -->
    <InlineMessage severity="success">
      <strong>{{ currentUserUsername }}</strong
      ><br />
      {{ currentUserRole }}<br />
    </InlineMessage>

    <!-- CENTER -->
    <div
      v-if="isUserLoggedSuperAdminOrAdmin()"
      class="absolute left-1/2 -translate-x-1/2 flex items-center gap-2"
    >
      <!-- CUSTOMER -->
      <div class="w-96">
        <MyAutocompletePaginated
          v-model="localCustomerSelected"
          option-label="companyName"
          placeholder="Cerca cliente iniziando a scrivere..."
          api-endpoint="users"
          label=""
        />
      </div>
      <!-- DEVICE -->
      <div class="w-96">
        <AutoComplete
          v-model="localDeviceSelected"
          fluid
          dropdown
          option-label="serialNumber"
          placeholder="Cerca dispositivo iniziando a scrivere..."
          :suggestions="userDevices"
          :disabled="localCustomerSelected == null || userDevices.length == 0"
          @complete="filterDevices"
        >
          <template #option="{ option }">
            <span class="font-semibold">{{ option.serialNumber }}</span>
            <span class="ml-2 text-sm text-gray-500">{{
              option.vehicleId
            }}</span>
          </template>
          <template v-if="localDeviceSelected" #dropdownicon>
            <i
              class="pi pi-times cursor-pointer"
              @click.stop="localDeviceSelected = null"
            />
          </template>
        </AutoComplete>
      </div>
      <!-- RESET -->
      <Button
        label="Reset"
        icon="pi pi-trash"
        align="right"
        outlined
        @click="onReset()"
      />
    </div>

    <!-- OTHER ACTIONS -->
    <div class="layout-topbar-actions">
      <!-- BUTTONS -->
      <div class="layout-config-menu">
        <!-- DARK - LIGHT MODE -->
        <button
          type="button"
          class="layout-topbar-action"
          @click="toggleDarkMode"
        >
          <i
            :class="['pi', { 'pi-moon': isDarkTheme, 'pi-sun': !isDarkTheme }]"
          />
        </button>

        <!-- COLOR SELECTOR -->
        <div v-if="isUserLoggedSuperAdmin()" class="relative">
          <button
            v-styleclass="{
              selector: '@next',
              enterFromClass: 'hidden',
              enterActiveClass: 'animate-scalein',
              leaveToClass: 'hidden',
              leaveActiveClass: 'animate-fadeout',
              hideOnOutsideClick: true
            }"
            type="button"
            class="layout-topbar-action layout-topbar-action-highlight"
          >
            <i class="pi pi-palette"></i>
          </button>
          <AppConfigurator />
        </div>
      </div>

      <button
        v-styleclass="{
          selector: '@next',
          enterFromClass: 'hidden',
          enterActiveClass: 'animate-scalein',
          leaveToClass: 'hidden',
          leaveActiveClass: 'animate-fadeout',
          hideOnOutsideClick: true
        }"
        class="layout-topbar-menu-button layout-topbar-action"
      >
        <i class="pi pi-ellipsis-v"></i>
      </button>

      <!-- PROFILE / LOGOUT BUTTONS -->
      <div class="layout-topbar-menu hidden lg:block">
        <div class="layout-topbar-menu-content">
          <Button
            icon="pi pi-user"
            rounded
            variant="outlined"
            aria-label="Profile"
            @click="onProfile"
          />
          <Button
            icon="pi pi-sign-out"
            rounded
            variant="outlined"
            aria-label="User"
            @click="onLogout($event)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
